/**
 * ctx query-parameter utilities.
 *
 * A URL carrying `?ctx=org:<id>` or `?ctx=network:<id>` tells the client to
 * switch into that context before rendering the page. These helpers parse and
 * strip the parameter.
 *
 * #404: server contract vingapi#465 — every notification and email link carries
 * `ctx` so the user lands in the right org/network.
 */

import type { ContextSwitchInput } from '@/types';

/** The query-string key. */
export const CTX_PARAM = 'ctx';

/**
 * Parse `?ctx=org:123` / `?ctx=network:456` from a search string.
 *
 * Accepts exactly `org:<positive integer>` or `network:<positive integer>`.
 * Anything else (missing, empty, bad format, zero, negative, float) → null.
 */
export function parseCtxParam(search: string): ContextSwitchInput | null {
  const params = new URLSearchParams(search);
  const raw = params.get(CTX_PARAM);
  if (!raw) return null;

  const match = /^(org|network):(\d+)$/.exec(raw);
  if (!match) return null;

  const type = match[1] as 'org' | 'network';
  const id = Number(match[2]);

  // Reject zero (not a valid id) and anything that parsed oddly.
  if (id <= 0 || !Number.isFinite(id)) return null;

  return { type, id };
}

/**
 * Return a location object with the `ctx` key removed from the search string.
 * Other params, their order, and the hash are preserved byte-for-byte.
 *
 * Returns the **object form** (`{ pathname, search, hash }`) so that
 * `navigate(obj, { replace: true })` satisfies the keepSearch guard
 * (redirects.keepSearch.guard.test.ts).
 */
export function stripCtxParam(location: {
  pathname: string;
  search: string;
  hash: string;
}): { pathname: string; search: string; hash: string } {
  const params = new URLSearchParams(location.search);
  params.delete(CTX_PARAM);
  const search = params.toString();
  return {
    pathname: location.pathname,
    search: search ? `?${search}` : '',
    hash: location.hash,
  };
}
